import ResponsiveImage from "@/components/ResponsiveImage";
import { useParams, Link } from "react-router-dom";
import { ArrowRight, Clock, Building2, MapPin, CheckCircle2 } from "lucide-react";
import Seo, { articleSchema, breadcrumbSchema, faqSchema, organizationSchema } from "@/components/Seo";
import Breadcrumbs from "@/components/Breadcrumbs";
import CtaSection from "@/components/sections/CtaSection";
import FaqSection from "@/components/sections/FaqSection";
import { getGuide, GUIDES } from "@/data/guides";
import { SERVICES } from "@/data/services";
import { GUIDE_VISUALS } from "@/pages/Kunskapsbank";
import PageNotFound from "@/lib/PageNotFound";
import { IMAGES } from "@/data/company";

const formatDate = (date) => new Intl.DateTimeFormat("sv-SE", { dateStyle: "long", timeZone: "UTC" }).format(new Date(`${date}T12:00:00Z`));

const SERVICE_VISUALS = {
  takbesiktning: IMAGES.inspection,
  "byta-tak": IMAGES.villaRoofDrone,
  takrenovering: IMAGES.teamUnderlay,
  takservice: IMAGES.eavesCraft,
  takmalning: IMAGES.roofPainting,
  platslageri: IMAGES.villaMetal,
  serviceavtal: IMAGES.brfTop,
  snoskottning: IMAGES.snowRemoval,
  "pris-pa-tak": IMAGES.craft,
  takmaterial: IMAGES.tileDetail,
  tegeltak: IMAGES.clayVilla,
  "takmaterial/betongpannor": IMAGES.concreteVilla,
  papptak: IMAGES.feltMaterial,
  plattak: IMAGES.darkVilla,
};

export default function GuidePage() {
  const { slug } = useParams();
  const guide = getGuide(slug);
  if (!guide) return <PageNotFound embedded />;

  const path = `/kunskapsbank/${guide.slug}/`;
  const image = GUIDE_VISUALS[guide.slug] || guide.image;
  const crumbs = [
    { name: "Kunskapsbank", path: "/kunskapsbank/" },
    { name: guide.h1, path },
  ];
  const relatedServices = (guide.relatedServices || []).map((s) => SERVICES.find((sv) => sv.slug === s)).filter(Boolean).slice(0, 3);
  const relatedGuides = (guide.relatedGuideSlugs || []).map(slug => GUIDES.find(item => item.slug === slug)).filter(Boolean).slice(0, 3);

  const schema = [
    organizationSchema(),
    articleSchema(path, guide.title, guide.metaDescription, guide.publishedAt, guide.updatedAt, image),
    breadcrumbSchema(path, crumbs),
    faqSchema(guide.faq),
  ].filter(Boolean);

  return (
    <>
      <Seo title={guide.title} description={guide.metaDescription} path={path} schema={schema} image={image} type="article" />
      <Breadcrumbs items={crumbs} />

      <section className="relative isolate overflow-hidden bg-white text-midnight">
        <ResponsiveImage src={image} alt={guide.h1} className="absolute inset-0 h-full w-full object-cover" fetchPriority="high" />
        <div className="absolute inset-0 bg-[linear-gradient(180deg,rgba(255,255,255,.98)_0%,rgba(255,255,255,.91)_76%,rgba(255,255,255,.58)_100%)] lg:bg-[linear-gradient(90deg,rgba(255,255,255,.99)_0%,rgba(255,255,255,.93)_58%,rgba(255,255,255,.22)_100%)]" />
        <div className="container-tisab relative grid gap-10 py-14 sm:py-20 lg:grid-cols-[.7fr_.3fr] lg:items-end lg:py-24">
          <div className="max-w-4xl">
            <p className="eyebrow !text-[#0a965c]">Kunskapsbank · TISAB</p>
            <h1 className="mt-5 font-heading text-5xl font-extrabold leading-[.94] tracking-[-.04em] sm:text-6xl lg:text-7xl text-balance">{guide.h1}</h1>
            <p className="mt-6 max-w-3xl text-lg leading-relaxed text-foreground/72">{guide.intro}</p>
          </div>
          <div className="border border-midnight/10 bg-white/92 p-5 shadow-[0_18px_45px_rgba(6,7,8,.10)] backdrop-blur-sm">
            <span className="flex items-center gap-2 text-xs text-foreground/70"><Building2 className="h-4 w-4 text-[#0a965c]" /> Takrenoverarna i Stockholm AB</span>
            <span className="mt-3 flex items-center gap-2 text-xs text-foreground/70"><Clock className="h-4 w-4 text-[#0a965c]" /> Uppdaterad <time dateTime={guide.updatedAt}>{formatDate(guide.updatedAt)}</time></span>
          </div>
        </div>
      </section>

      <section className="border-b border-midnight/10 bg-[#e8f3ed]">
        <div className="container-tisab grid gap-7 py-10 lg:grid-cols-[.26fr_.74fr] lg:items-start">
          <p className="eyebrow">Kort svar</p>
          <p className="max-w-4xl font-heading text-2xl font-extrabold leading-snug text-midnight sm:text-3xl">{guide.answer}</p>
        </div>
      </section>

      <section className="section-pad bg-parchment">
        <div className="container-tisab grid gap-10 lg:grid-cols-[.7fr_.3fr]">
          <article className="max-w-4xl">
            {guide.sections.map((section, index) => (
              <section key={section.h2} id={`del-${index + 1}`} className="mb-12 border-t border-midnight/10 pt-8 first:border-t-0 first:pt-0">
                <span className="font-mono text-[10px] font-bold text-tisab">{String(index + 1).padStart(2, "0")}</span>
                <h2 className="mt-3 font-heading text-3xl font-extrabold leading-tight text-midnight sm:text-4xl">{section.h2}</h2>
                {section.body.split("\n\n").map((paragraph, index) => <p key={index} className="mt-5 text-base leading-[1.85] text-foreground/76">{paragraph}</p>)}
                {section.bullets?.length > 0 && <ul className="mt-5 list-disc space-y-3 pl-6 text-base leading-relaxed text-foreground/76">{section.bullets.map(item => <li key={item}>{item}</li>)}</ul>}
                {section.table && <div className="mt-6 overflow-x-auto rounded border border-midnight/15" tabIndex={0} role="region" aria-label={`Tabell: ${section.h2}`}>
                  <table className="w-full min-w-[480px] border-collapse text-left text-sm">
                    <caption className="sr-only">{section.h2}</caption>
                    <thead className="bg-[#e8f3ed]"><tr>{section.table.columns.map(column => <th key={column} scope="col" className="border-b border-midnight/15 p-4 font-bold">{column}</th>)}</tr></thead>
                    <tbody>{section.table.rows.map((row, index) => <tr key={index} className="border-b border-midnight/10 last:border-0">{row.map((cell, column) => column === 0 ? <th key={column} scope="row" className="p-4 font-semibold">{cell}</th> : <td key={column} className="p-4 leading-relaxed">{cell}</td>)}</tr>)}</tbody>
                  </table>
                </div>}
              </section>
            ))}
            {guide.sources?.length > 0 && <section id="kallor" className="border-t border-midnight/15 pt-8">
              <h2 className="font-heading text-2xl font-bold">Källor och vidare läsning</h2>
              <ul className="mt-5 list-disc space-y-3 pl-6">{guide.sources.map(source => <li key={source.url}><a className="text-tisab underline underline-offset-4" href={source.url} target="_blank" rel="noopener noreferrer">{source.label}</a></li>)}</ul>
              <p className="mt-5 text-sm leading-relaxed text-foreground/65">TISAB ansvarar för innehållet. Guiden beskriver allmänna förutsättningar; omfattningen för en viss fastighet behöver bedömas utifrån taket och det aktuella uppdraget.</p>
            </section>}
          </article>

          <aside className="lg:sticky lg:top-28 lg:h-fit">
            <div className="border border-midnight/10 bg-white p-6">
              <p className="eyebrow">I den här guiden</p>
              <nav className="mt-5 grid gap-2">
                {guide.sections.map((section, index) => (
                  <a key={section.h2} href={`#del-${index + 1}`} className="flex items-start gap-3 border-t border-midnight/8 pt-3 text-sm font-semibold text-foreground/70 first:border-t-0 first:pt-0 hover:text-tisab">
                    <span className="font-mono text-[9px] text-tisab">0{index + 1}</span>{section.h2}
                  </a>
                ))}
              </nav>
            </div>
            <div className="mt-4 bg-[#087a4d] p-6 text-white">
              <CheckCircle2 className="h-6 w-6 text-[#0a965c]" />
              <h2 className="mt-5 font-heading text-2xl font-extrabold">Behöver du ett svar för just ditt tak?</h2>
              <p className="mt-3 text-sm leading-relaxed text-white/64">Skicka adress, bilder och frågan. Då går det att avgränsa ett relevant nästa steg.</p>
              <Link to="/kontakta-oss/" className="mt-6 inline-flex items-center gap-2 text-sm font-bold text-white underline underline-offset-4">Begär en bedömning <ArrowRight className="h-4 w-4" /></Link>
            </div>
          </aside>
        </div>
      </section>

      {relatedServices.length > 0 && (
        <section className="section-pad bg-white">
          <div className="container-tisab">
            <p className="eyebrow">Från guide till handling</p>
            <h2 className="mt-3 font-heading text-4xl font-extrabold text-midnight sm:text-5xl">Tjänster som hör ihop med frågan.</h2>
            <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {relatedServices.map((service) => (
                <Link key={service.path} to={service.path} className="group overflow-hidden border border-midnight/10 bg-parchment">
                  <div className="overflow-hidden bg-midnight"><ResponsiveImage src={SERVICE_VISUALS[service.slug] || service.image} alt={service.h1} className="h-52 w-full object-cover transition-transform duration-700 group-hover:scale-[1.035]" loading="lazy" /></div>
                  <div className="p-6">
                    <h3 className="font-heading text-2xl font-extrabold text-midnight">{service.h1}</h3>
                    <p className="mt-3 line-clamp-2 text-sm leading-relaxed text-foreground/70">{service.intro}</p>
                    <span className="mt-5 inline-flex items-center gap-2 text-sm font-bold text-tisab">Läs om tjänsten <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" /></span>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      <section className="border-y border-midnight/10 bg-[#edf3ef] py-12 sm:py-16">
        <div className="container-tisab grid gap-7 lg:grid-cols-[.34fr_.66fr] lg:items-center">
          <div>
            <p className="eyebrow">Lokala vägar</p>
            <h2 className="mt-3 font-heading text-3xl font-extrabold text-midnight">Hjälp nära fastigheten.</h2>
          </div>
          <div className="flex flex-wrap gap-2">
            {[["stockholm","Stockholm"],["lidingo","Lidingö"],["nacka","Nacka"],["solna","Solna"],["danderyd","Danderyd"],["taby","Täby"]].map(([locationSlug, label]) => (
              <Link key={locationSlug} to={`/taklaggare/${locationSlug}/`} className="inline-flex min-h-11 items-center gap-2 border border-midnight/12 bg-white px-4 text-sm font-semibold text-midnight hover:border-tisab hover:text-tisab"><MapPin className="h-3.5 w-3.5" /> {label}</Link>
            ))}
          </div>
        </div>
      </section>

      <FaqSection faq={guide.faq} title={`Vanliga frågor om ${guide.h1.toLowerCase()}`} />

      <section className="section-pad bg-parchment">
        <div className="container-tisab">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
            <div>
              <p className="eyebrow">Fortsätt läsa</p>
              <h2 className="mt-3 font-heading text-4xl font-extrabold text-midnight">Nästa relevanta guide.</h2>
            </div>
            <Link to="/kunskapsbank/" className="inline-flex items-center gap-2 text-sm font-bold text-tisab">Hela kunskapsbanken <ArrowRight className="h-4 w-4" /></Link>
          </div>
          <div className="mt-8 grid gap-4 sm:grid-cols-3">
            {relatedGuides.map((related) => (
              <Link key={related.slug} to={`/kunskapsbank/${related.slug}/`} className="group overflow-hidden border border-midnight/10 bg-white">
                <div className="overflow-hidden bg-midnight"><ResponsiveImage src={GUIDE_VISUALS[related.slug] || related.image} alt={related.h1} className="h-44 w-full object-cover transition-transform duration-700 group-hover:scale-[1.035]" loading="lazy" /></div>
                <div className="p-5">
                  <h3 className="font-heading text-xl font-extrabold text-midnight">{related.h1}</h3>
                  <span className="mt-4 inline-flex items-center gap-2 text-xs font-bold text-tisab">Läs guiden <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-1" /></span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <CtaSection title="Nästa steg är takets eget" text="Använd guiden som startpunkt och skicka sedan adress, bilder och det du behöver få klarhet i." />
    </>
  );
}